import { useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Airframe } from "../../../../types/airframe";
import { Component } from "../../../../types/component";
import { AirframeAssemblyCell } from "./AirframeAssemblyCell";
import {
  buildAssemblySlotColumns,
  findSlotForColumn,
  getInstalledComponentForSlot,
} from "./airframeAssemblyGridColumns";

type Props = {
  airframe: Airframe;
  components: Component[];
  getInstalledComponentsForAirframe: (airframe: Airframe) => Component[];
  onDetach: (componentId: string, airframeId?: string, slotId?: string) => void;
  canDetach: boolean;
};

const subGridTableSx = {
  "& .generic-table-header-cell": {
    backgroundColor: "hsl(205 45% 22%) !important",
    color: "hsl(205 85% 88%) !important",
    borderBottom: "2px solid hsl(205 45% 38%) !important",
    whiteSpace: "nowrap",
    fontSize: "0.8rem",
  },
  "& .generic-table-body-cell": {
    fontSize: "0.85rem",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    backgroundColor: "#1a1a1a",
  },
};

export function AirframeAssemblySubGrid({
  airframe,
  components,
  getInstalledComponentsForAirframe,
  onDetach,
  canDetach,
}: Props) {
  const assemblyColumns = useMemo(
    () => buildAssemblySlotColumns([airframe]),
    [airframe]
  );

  const installedForRow = useMemo(
    () => getInstalledComponentsForAirframe(airframe),
    [airframe, getInstalledComponentsForAirframe]
  );

  if (assemblyColumns.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#888", py: 1 }}>
        No engine, APU, or landing gear positions configured for this airframe.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ bgcolor: "#1a1a1a", border: "1px solid #333" }}>
      <Table size="small" className="airframe-assembly-subgrid">
        <TableHead>
          <TableRow>
            {assemblyColumns.map((column) => (
              <TableCell
                key={column.id}
                className="generic-table-header-cell"
                sx={{ minWidth: 150 }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {assemblyColumns.map((column) => {
              const slot = findSlotForColumn(airframe, column);
              const installed = slot
                ? getInstalledComponentForSlot(
                    airframe,
                    slot,
                    installedForRow,
                    components
                  )
                : undefined;

              return (
                <TableCell key={column.id} className="generic-table-body-cell">
                  <AirframeAssemblyCell
                    component={installed}
                    onDetach={onDetach}
                    canDetach={canDetach}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Inline detail block shown directly under an airframe row in the main grid. */
export function AirframeAssemblySubGridInline({
  airframe,
  components,
  getInstalledComponentsForAirframe,
  onDetach,
  canDetach,
}: Props) {
  return (
    <Box
      className="airframe-assembly-subgrid-inline"
      sx={{
        py: 1.5,
        px: 2,
        bgcolor: "#1a1a1a",
        borderLeft: "3px solid hsl(19 101% 51% / 1)",
        ...subGridTableSx,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: "hsl(19 101% 51% / 1)", mb: 1, fontWeight: 600 }}
      >
        Attached assemblies — {airframe.tailNumber}
      </Typography>
      <AirframeAssemblySubGrid
        airframe={airframe}
        components={components}
        getInstalledComponentsForAirframe={getInstalledComponentsForAirframe}
        onDetach={onDetach}
        canDetach={canDetach}
      />
    </Box>
  );
}
